import React from "react";
import LegalLayout, { LegalSection, LegalList } from "./LegalLayout";

export default function DPA() {
  return (
    <LegalLayout
      badge="Legal"
      title="Data Processing Agreement"
      subtitle="Art. 28 GDPR — Governing the processing of personal data by Vawick on behalf of its customers."
      lastUpdated="June 2026"
    >
      <LegalSection title="Parties">
        <p>
          <strong className="text-white">Data Processor:</strong> Vawick Consulting UG
          (haftungsbeschränkt), [Address], Germany ("Processor")
        </p>
        <p>
          <strong className="text-white">Data Controller:</strong> The Customer who has entered
          into a subscription agreement with Vawick for use of the nexus-vs platform ("Controller")
        </p>
        <p>
          This Data Processing Agreement ("DPA") supplements the Terms of Service between the
          parties and governs the processing of personal data by the Processor on behalf of the
          Controller, as required by Art. 28 GDPR. In the event of a conflict between this DPA
          and the Terms of Service, this DPA shall prevail with respect to data processing matters.
        </p>
      </LegalSection>

      <LegalSection title="1. Subject Matter and Duration">
        <p>
          The Processor processes personal data on behalf of the Controller for the purpose of
          providing the nexus-vs contractor management platform, in accordance with the Terms of
          Service. Processing shall continue for the duration of the service agreement and cease
          upon termination.
        </p>
      </LegalSection>

      <LegalSection title="2. Nature and Purpose of Processing">
        <p>Processing activities include:</p>
        <LegalList items={[
          "Storing and managing contractor profiles, contact information, and employment data",
          "Processing time records, project assignments, and approval workflows",
          "Generating and storing contracts with digital signature records",
          "Facilitating payroll calculations, payout records, and invoice management",
          "Compliance document storage, validation, and expiry monitoring",
          "Access management, session logging, and audit trail generation",
        ]} />
      </LegalSection>

      <LegalSection title="3. Categories of Data Subjects and Personal Data">
        <p>
          <strong className="text-white">Data subjects:</strong> Independent contractors,
          freelancers, and employees managed through the nexus-vs platform on behalf of
          the Controller.
        </p>
        <p><strong className="text-white">Categories of personal data processed:</strong></p>
        <LegalList items={[
          "Identification data: full name, date of birth, nationality",
          "Contact data: email address, phone number, postal address",
          "Employment data: contract terms, project assignments, hourly/daily rates",
          "Financial data: bank account details (IBAN/BIC), payout amounts, invoice records",
          "Document data: identity documents, tax declarations, compliance certificates",
          "Activity data: time tracking records, login events, approval history, audit logs",
        ]} />
        <p>
          <strong className="text-white">Special categories of personal data:</strong> The
          Controller shall not upload data falling under Art. 9 GDPR (e.g. health data, biometric
          data) without express prior written agreement with the Processor.
        </p>
      </LegalSection>

      <LegalSection title="4. Obligations of the Processor">
        <p>The Processor shall:</p>
        <LegalList items={[
          "Process personal data only on documented instructions from the Controller, unless required otherwise by Union or Member State law",
          "Ensure that all persons authorised to process the personal data are bound by a duty of confidentiality",
          "Implement the technical and organisational measures described in Annex A of this DPA",
          "Not engage any sub-processor without prior specific or general written authorisation from the Controller",
          "Assist the Controller in responding to requests from data subjects exercising their GDPR rights, to the extent reasonably practicable",
          "Assist the Controller in meeting its obligations under Arts. 32–36 GDPR regarding security, breach notification, DPIAs, and prior consultation",
          "At the Controller's written request and choice, securely delete or return all personal data after the end of service provision, and confirm deletion in writing",
          "Provide the Controller with all information reasonably necessary to demonstrate compliance with Art. 28 GDPR, and permit and cooperate in audits",
        ]} />
      </LegalSection>

      <LegalSection title="5. Sub-Processors">
        <p>
          The Controller provides general authorisation for the Processor to engage the sub-processors
          listed below. The Processor shall inform the Controller of any intended changes (additions
          or replacements) with at least 30 days' prior written notice, giving the Controller the
          opportunity to raise a reasonable objection.
        </p>

        <div className="rounded-xl border border-[#152222] overflow-hidden mt-4 not-prose">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-[#152222] bg-[#0A1212]">
                <th className="text-left px-4 py-3 text-white font-medium">Sub-processor</th>
                <th className="text-left px-4 py-3 text-white font-medium">Purpose</th>
                <th className="text-left px-4 py-3 text-white font-medium">Location</th>
              </tr>
            </thead>
            <tbody className="text-[#9BB8B8]">
              <tr className="border-b border-[#0E1A1A]">
                <td className="px-4 py-3 text-white">Supabase, Inc.</td>
                <td className="px-4 py-3">Database, authentication, file storage</td>
                <td className="px-4 py-3">EU (Frankfurt, Germany)</td>
              </tr>
              <tr>
                <td className="px-4 py-3 text-white">[Hosting Provider]</td>
                <td className="px-4 py-3">Server infrastructure and delivery</td>
                <td className="px-4 py-3">EU</td>
              </tr>
            </tbody>
          </table>
        </div>

        <p>
          Each sub-processor is subject to a data processing agreement with the Processor
          providing at least equivalent data protection obligations as set out in this DPA.
        </p>
      </LegalSection>

      <LegalSection title="6. International Data Transfers">
        <p>
          All personal data is processed within the European Union. In the event that any
          sub-processor transfer to a third country (outside the EU/EEA) becomes necessary,
          the Processor shall ensure adequate safeguards are in place pursuant to Chapter V
          GDPR, including Standard Contractual Clauses (SCCs) as approved by the European
          Commission, or an applicable adequacy decision.
        </p>
      </LegalSection>

      <LegalSection title="7. Technical and Organisational Measures (Annex A — TOMs)">
        <p>
          The Processor implements the following measures pursuant to Art. 32 GDPR to ensure
          a level of security appropriate to the risk:
        </p>

        <p><strong className="text-white">Confidentiality</strong></p>
        <LegalList items={[
          "Encryption of personal data at rest using AES-256",
          "Encryption of personal data in transit using TLS 1.3",
          "Role-Based Access Control (RBAC) — access restricted to authorised personnel with a documented need",
          "Row Level Security (RLS) — per-tenant data isolation enforced at database level",
          "Multi-factor authentication required for all administrative and privileged access",
          "Strict Content Security Policy (CSP) headers enforced on the application layer",
        ]} />

        <p><strong className="text-white">Integrity and Availability</strong></p>
        <LegalList items={[
          "Automated daily database backups with point-in-time recovery capability",
          "Rate limiting and DDoS mitigation controls at the edge",
          "Session timeout and continuous session integrity monitoring",
          "Defined incident response procedures with escalation paths",
          "Redundant infrastructure to minimise unplanned downtime",
        ]} />

        <p><strong className="text-white">Organisational Measures</strong></p>
        <LegalList items={[
          "Production data access restricted to personnel with a documented business need",
          "All staff with access to personal data bound by contractual confidentiality obligations",
          "Regular security training and awareness for relevant employees",
          "Documented data breach notification procedure",
          "SOC-2 Type II audit actively in progress",
        ]} />
      </LegalSection>

      <LegalSection title="8. Personal Data Breach Notification">
        <p>
          The Processor shall notify the Controller without undue delay — and in any event
          within <strong className="text-white">48 hours</strong> of becoming aware — of any
          personal data breach affecting the Controller's data. Notification shall include:
        </p>
        <LegalList items={[
          "A description of the nature of the breach, including categories and approximate number of data subjects and personal data records affected",
          "The name and contact details of the data protection contact point",
          "A description of the likely consequences of the breach",
          "The measures taken or proposed to address the breach",
        ]} />
        <p>
          Where not all information can be provided at once, it may be provided in phases
          without undue further delay.
        </p>
      </LegalSection>

      <LegalSection title="9. Data Deletion and Return">
        <p>
          Upon termination of the service agreement, the Processor shall, at the Controller's
          written choice, either securely delete or return all personal data. The Controller
          has 30 days following termination to request data export via the platform or by
          contacting support@vawick.de, after which automated secure deletion is performed.
          A written deletion confirmation is available upon request.
        </p>
      </LegalSection>

      <LegalSection title="10. Audit Rights">
        <p>
          The Controller may audit the Processor's compliance with this DPA at most once per
          calendar year, with at least 30 days' prior written notice. Audits shall be conducted
          during normal business hours and in a manner that minimises operational disruption.
        </p>
        <p>
          The Processor may satisfy audit obligations by providing certified third-party audit
          reports (e.g. SOC-2 Type II reports once available) in lieu of on-site audits, unless
          the Controller reasonably demonstrates that such reports are insufficient.
        </p>
      </LegalSection>

      <LegalSection title="11. Liability">
        <p>
          Each party shall be liable for damage caused by processing that infringes GDPR in
          accordance with Art. 82 GDPR. The total aggregate liability of the Processor under
          this DPA shall not exceed the liability cap set out in the Terms of Service.
        </p>
      </LegalSection>

      <LegalSection title="12. Governing Law">
        <p>
          This DPA is governed by German law and is subject to the jurisdiction set out in
          the Terms of Service.
        </p>
        <p>
          To request a countersigned copy of this DPA, or to discuss custom DPA terms for
          enterprise agreements, contact{" "}
          <a
            href="mailto:support@vawick.de"
            className="text-white hover:text-[#FF4F00] transition-colors"
          >
            support@vawick.de
          </a>.
        </p>
      </LegalSection>
    </LegalLayout>
  );
}
